import React, { useState, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { getData, postData } from "../../utils/api";

function formatDateTime(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateTimeIST(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function toDatetimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

function generateRequestId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Parse datetime-local value (YYYY-MM-DDTHH:mm or YYYY-MM-DD) as local time and return ISO UTC string.
// Avoids "date-only" strings being parsed as UTC midnight, which caused wrong dates in history.
function datetimeLocalToISO(value) {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!match) return new Date(trimmed).toISOString();
  const [, y, m, d, h = "0", min = "0"] = match;
  const date = new Date(
    Number(y),
    Number(m) - 1,
    Number(d),
    Number(h),
    Number(min),
    0,
    0
  );
  return date.toISOString();
}

const ManualPollByDateRange = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const platform = useMemo(() => {
    const p = (searchParams.get("platform") || "amazon").toLowerCase();
    return p === "shopify" || p === "flipkart" ? p : "amazon";
  }, [searchParams]);

  const todayDate = new Date().toISOString().split("T")[0];
  const maxDateTime = `${todayDate}T23:59`;

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [pollHistory, setPollHistory] = useState(null);
  const [loadingFromLastPoll, setLoadingFromLastPoll] = useState(false);

  const pollIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const currentRequestIdRef = useRef(null);

  const handleSetFromLastPoll = async () => {
    setLoadingFromLastPoll(true);
    try {
      const res = await getData(
        `/data/webhook/manual-poll-history?page=1&limit=1&platform=${encodeURIComponent(platform)}`
      );
      if (res?.status && res?.data?.rows?.length > 0) {
        const latest = res.data.rows[0];
        const fromDate = latest.maxPurchaseDate || latest.dateTo;
        if (fromDate) {
          setDateFrom(toDatetimeLocal(fromDate));
        }
      }
    } catch (err) {
      console.error("Failed to load last poll", err);
    } finally {
      setLoadingFromLastPoll(false);
    }
  };

  const handleStopFetch = async () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    const requestId = currentRequestIdRef.current;
    if (requestId) {
      try {
        await postData("/data/polling/manual-poll-by-date/cancel", {
          requestId,
        });
      } catch (_) {}
      currentRequestIdRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setFetching(false);
  };

  const handleFetch = async () => {
    setFetchError(null);
    setPollHistory(null);
    setFetching(true);
    abortControllerRef.current = new AbortController();
    const requestId = generateRequestId();
    currentRequestIdRef.current = requestId;

    const stopPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };

    const finishIfTerminal = (history) => {
      const status = history?.status;
      if (status === "success" || status === "failed" || status === "cancelled") {
        stopPolling();
        setFetching(false);
        abortControllerRef.current = null;
        if (status === "failed") {
          setFetchError(history?.errorMessage || "Manual poll failed");
        }
        return true;
      }
      return false;
    };

    const fetchHistoryByRequestId = async () => {
      try {
        const historyRes = await getData(
          `/data/polling/manual-poll-history/by-request-id/${encodeURIComponent(requestId)}`
        );
        if (historyRes?.status && historyRes?.data) {
          setPollHistory(historyRes.data);
          finishIfTerminal(historyRes.data);
        }
      } catch (_) {}
    };

    try {
      // Backend returns 202 immediately and continues in background; progress comes from history polling.
      pollIntervalRef.current = setInterval(fetchHistoryByRequestId, 2500);
      const response = await postData(
        "/data/polling/manual-poll-by-date",
        {
          dateFrom: datetimeLocalToISO(dateFrom),
          dateTo: datetimeLocalToISO(dateTo),
          platform,
          requestId,
        },
        { signal: abortControllerRef.current?.signal }
      );
      if (!(response?.status && response?.data)) {
        stopPolling();
        setFetchError(response?.message || "Manual poll failed");
        setFetching(false);
        abortControllerRef.current = null;
        return;
      }
      // Kick an immediate history fetch; keep interval until terminal status.
      await fetchHistoryByRequestId();
    } catch (err) {
      if (err?.name === "AbortError" || err?.name === "CanceledError") {
        stopPolling();
        setFetching(false);
        abortControllerRef.current = null;
        return;
      }
      // Start may have succeeded before a proxy timeout; keep polling history if requestId is known.
      const stillActive = currentRequestIdRef.current === requestId;
      if (stillActive) {
        await fetchHistoryByRequestId();
        // If history never appears, surface the original error after a short wait.
        setTimeout(async () => {
          if (!pollIntervalRef.current) return;
          try {
            const historyRes = await getData(
              `/data/polling/manual-poll-history/by-request-id/${encodeURIComponent(requestId)}`
            );
            if (!(historyRes?.status && historyRes?.data)) {
              stopPolling();
              setFetchError(
                err?.response?.data?.message || err?.message || "Request failed"
              );
              setFetching(false);
              abortControllerRef.current = null;
            }
          } catch (_) {
            stopPolling();
            setFetchError(
              err?.response?.data?.message || err?.message || "Request failed"
            );
            setFetching(false);
            abortControllerRef.current = null;
          }
        }, 3000);
        return;
      }
      stopPolling();
      setFetchError(
        err?.response?.data?.message || err?.message || "Request failed"
      );
      setFetching(false);
      abortControllerRef.current = null;
    }
  };

  const failedIds = pollHistory?.failedOrderIds
    ? Array.isArray(pollHistory.failedOrderIds)
      ? pollHistory.failedOrderIds
      : []
    : [];
  const insertedIds = pollHistory?.insertedOrderIds
    ? Array.isArray(pollHistory.insertedOrderIds)
      ? pollHistory.insertedOrderIds
      : []
    : [];

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <button
          type="button"
          onClick={() =>
            navigate(
              `/analytics/manual-polling?platform=${encodeURIComponent(platform)}`,
            )
          }
          className="text-gray-600 hover:text-gray-900  flex items-center gap-1"
        >
          ← Back to Manual Polling
        </button>
      </div>
      <h1 className="text-2xl font-bold mb-2">Manual Poll by Date Range</h1>
      <p className="text-sm text-gray-600 mb-4">
        Select date and time range to import orders from{" "}
        <span className="font-medium capitalize">{platform}</span>. Progress is
        saved under Manual Poll History (poll by request id).
      </p>
      <div className="flex flex-wrap gap-2 mb-6 text-sm">
        <span className="text-gray-600 self-center">Platform:</span>
        <button
          type="button"
          onClick={() => navigate("/analytics/manual-poll-by-date?platform=amazon")}
          className={`px-3 py-1 rounded-md border ${
            platform === "amazon"
              ? "bg-blue-600 text-white border-blue-600"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          Amazon
        </button>
        <button
          type="button"
          onClick={() => navigate("/analytics/manual-poll-by-date?platform=shopify")}
          className={`px-3 py-1 rounded-md border ${
            platform === "shopify"
              ? "bg-emerald-700 text-white border-emerald-700"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          Shopify
        </button>
        <button
          type="button"
          onClick={() => navigate("/analytics/manual-poll-by-date?platform=flipkart")}
          className={`px-3 py-1 rounded-md border ${
            platform === "flipkart"
              ? "bg-amber-600 text-white border-amber-600"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          Flipkart
        </button>
      </div>

      {platform === "flipkart" && (
        <p className="text-xs text-gray-500 mb-4 max-w-xl">
          Pending, shipped, and delivered Flipkart shipments are matched by
          dispatch-after date. Cancelled shipments in the same range are
          included, matched by order date and cancellation date. Existing
          orders are skipped, so overlapping ranges are safe.
        </p>
      )}

      {/* Top section: date time + Fetch */}
      <div className="max-w-md space-y-4 mb-10">
        <div>
          <label
            htmlFor="date-from"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Date & time from
          </label>
          <div className="flex gap-2 items-center">
            <input
              id="date-from"
              type="datetime-local"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={maxDateTime}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
            />
            <button
              type="button"
              onClick={handleSetFromLastPoll}
              disabled={loadingFromLastPoll}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50 whitespace-nowrap"
            >
              {loadingFromLastPoll ? "..." : "From last poll"}
            </button>
          </div>
        </div>
        <div>
          <label
            htmlFor="date-to"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Date & time to
          </label>
          <input
            id="date-to"
            type="datetime-local"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            min={dateFrom || undefined}
            max={maxDateTime}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
          />
        </div>
        {fetchError && (
          <p className="text-sm text-red-600">{fetchError}</p>
        )}
        <div className="pt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={handleFetch}
            disabled={fetching || !dateFrom || !dateTo}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {fetching ? "Fetching..." : "Fetch"}
          </button>
          {fetching && (
            <button
              type="button"
              onClick={handleStopFetch}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Stop
            </button>
          )}
        </div>
      </div>

      {/* Stats section (from ManualPollHistory) */}
      {pollHistory && (
        <>
          <h2 className="text-lg font-semibold mb-3">Poll stats</h2>
          <div className="overflow-x-auto rounded border border-gray-200 mb-6">
            <table className="min-w-full bg-white text-sm">
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-medium text-gray-700 w-48">
                    Date from
                  </td>
                  <td className="px-3 py-2 text-gray-900">
                    {typeof pollHistory.dateFrom === "string" ? (
                      <span className="block space-y-1">
                        <span className="font-mono text-xs text-gray-600 block">
                          ISO: {pollHistory.dateFrom}
                        </span>
                        <span>IST: {formatDateTimeIST(pollHistory.dateFrom)}</span>
                      </span>
                    ) : (
                      pollHistory.dateFrom
                    )}
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-medium text-gray-700">
                    Date to
                  </td>
                  <td className="px-3 py-2 text-gray-900">
                    {typeof pollHistory.dateTo === "string" ? (
                      <span className="block space-y-1">
                        <span className="font-mono text-xs text-gray-600 block">
                          ISO: {pollHistory.dateTo}
                        </span>
                        <span>IST: {formatDateTimeIST(pollHistory.dateTo)}</span>
                      </span>
                    ) : (
                      pollHistory.dateTo
                    )}
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-medium text-gray-700">
                    Platform
                  </td>
                  <td className="px-3 py-2 capitalize text-gray-900">
                    {pollHistory.platform}
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-medium text-gray-700">
                    Status
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        pollHistory.status === "success"
                          ? "text-green-600"
                          : pollHistory.status === "failed"
                            ? "text-red-600"
                            : pollHistory.status === "cancelled"
                              ? "text-gray-600"
                              : "text-amber-600"
                      }
                    >
                      {pollHistory.status}
                    </span>
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-medium text-gray-700">
                    Total orders fetched
                  </td>
                  <td className="px-3 py-2 text-gray-900">
                    {pollHistory.totalOrdersFetched ?? "—"}
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-medium text-gray-700">
                    Created
                  </td>
                  <td className="px-3 py-2 text-gray-900">
                    {pollHistory.createdCount ?? "—"}
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-medium text-gray-700">
                    Skipped / failed count
                  </td>
                  <td className="px-3 py-2 text-gray-900">
                    {pollHistory.skippedCount ?? "—"}
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-medium text-gray-700">
                    Triggered at
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {formatDateTimeIST(pollHistory.createdAt)}
                  </td>
                </tr>
                {pollHistory.errorMessage && (
                  <tr className="border-b border-gray-200">
                    <td className="px-3 py-2 font-medium text-gray-700">
                      Error
                    </td>
                    <td className="px-3 py-2 text-red-600">
                      {pollHistory.errorMessage}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Inserted order IDs */}
          <h2 className="text-lg font-semibold mb-3">Inserted order IDs</h2>
          {insertedIds.length === 0 ? (
            <p className="text-sm text-gray-500 mb-6">
              No inserted orders.
            </p>
          ) : (
            <div className="overflow-x-auto rounded border border-gray-200 mb-6">
              <table className="min-w-full bg-white text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      #
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Order ID
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {insertedIds.map((orderId, index) => (
                    <tr
                      key={`${orderId}-${index}`}
                      className="border-b border-gray-100"
                    >
                      <td className="px-3 py-2 text-gray-600">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 text-gray-900 font-mono">
                        {orderId}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Failed list */}
          <h2 className="text-lg font-semibold mb-3">Failed order IDs</h2>
          {failedIds.length === 0 ? (
            <p className="text-sm text-gray-500 mb-6">
              No failed orders.
            </p>
          ) : (
            <div className="overflow-x-auto rounded border border-gray-200 mb-6">
              <table className="min-w-full bg-white text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      #
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                      Order ID
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {failedIds.map((orderId, index) => (
                    <tr
                      key={`${orderId}-${index}`}
                      className="border-b border-gray-100"
                    >
                      <td className="px-3 py-2 text-gray-600">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 text-gray-900 font-mono">
                        {orderId}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManualPollByDateRange;
